import React from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import Logo from '../Logo'

const FooterContainer = styled.footer`
  background: rgba(0, 0, 0, 0.3);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 4rem var(--container-padding-desktop) 2rem;

  @media (max-width: 1024px) {
    padding: 3rem var(--container-padding-tablet) 2rem;
  }

  @media (max-width: 768px) {
    padding: 2rem var(--container-padding-mobile) 1.5rem;
  }
`

const FooterContent = styled.div`
  max-width: var(--desktop);
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr repeat(3, 1fr);
  gap: 4rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 3rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }
`

const FooterSection = styled.div`
  h3 {
    color: var(--color-text);
    font-size: 1.25rem;
    margin-bottom: 1.5rem;
  }
`

const BrandSection = styled(FooterSection)`
  .logo-container {
    margin-bottom: 1.5rem;
  }

  p {
    color: var(--color-text-secondary);
    margin-bottom: 1.5rem;
    line-height: 1.6;
  }

  @media (max-width: 768px) {
    .logo-container {
      justify-content: center;
    }
  }
`

const LinkList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    margin-bottom: 0.75rem;

    a {
      color: var(--color-text-secondary);
      transition: color 0.2s ease;

      &:hover {
        color: var(--color-primary);
      }
    }
  }
`

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;

  @media (max-width: 768px) {
    justify-content: center;
  }
`

const SocialLink = styled.a`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text);
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-primary);
    transform: translateY(-3px);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`

const BottomBar = styled.div`
  margin-top: 4rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--color-text-secondary);
  font-size: 0.875rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
    margin-top: 2rem;
  }
`

const LegalLinks = styled.div`
  display: flex;
  gap: 2rem;

  a {
    color: var(--color-text-secondary);
    transition: color 0.2s ease;

    &:hover {
      color: var(--color-primary);
    }
  }

  @media (max-width: 768px) {
    flex-wrap: wrap;
    justify-content: center;
    gap: 1rem;
  }
`

export default function Footer() {
  return (
    <FooterContainer>
      <FooterContent>
        <BrandSection>
          <div className="logo-container">
            <Logo />
          </div>
          <p>
            Revolutionizing project development through AI-powered collaboration.
            Our platform enables seamless integration of AI agents for efficient
            and innovative solutions.
          </p>
          <SocialLinks>
            <SocialLink href="https://twitter.com/omnibrainai" target="_blank" rel="noopener noreferrer">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" />
              </svg>
            </SocialLink>
            <SocialLink href="https://github.com/omnibrainai" target="_blank" rel="noopener noreferrer">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.37 0 0 5.37 0 12c0 5.3 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61-.546-1.387-1.333-1.756-1.333-1.756-1.09-.745.083-.73.083-.73 1.205.085 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 21.795 24 17.295 24 12c0-6.63-5.37-12-12-12" />
              </svg>
            </SocialLink>
            <SocialLink href="https://discord.gg/omnibrainai" target="_blank" rel="noopener noreferrer">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z" />
              </svg>
            </SocialLink>
          </SocialLinks>
        </BrandSection>

        <FooterSection>
          <h3>Platform</h3>
          <LinkList>
            <li><Link to="/request">Request Project</Link></li>
            <li><Link to="/projects">My Projects</Link></li>
            <li><Link to="/guide">Guide</Link></li>
            <li><Link to="/api">API</Link></li>
          </LinkList>
        </FooterSection>

        <FooterSection>
          <h3>Resources</h3>
          <LinkList>
            <li><Link to="/whitepaper">Whitepaper</Link></li>
            <li><Link to="/docs">Documentation</Link></li>
            <li><a href="https://github.com/omnibrainai" target="_blank" rel="noopener noreferrer">GitHub</a></li>
            <li><Link to="/blog">Blog</Link></li>
          </LinkList>
        </FooterSection>

        <FooterSection>
          <h3>Company</h3>
          <LinkList>
            <li><Link to="/about">About</Link></li>
            <li><Link to="/careers">Careers</Link></li>
            <li><Link to="/contact">Contact</Link></li>
            <li><Link to="/press">Press Kit</Link></li>
          </LinkList>
        </FooterSection>
      </FooterContent>

      <BottomBar>
        <div>© {new Date().getFullYear()} OmniBrain. All rights reserved.</div>
        <LegalLinks>
          <Link to="/terms">Terms</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/cookies">Cookies</Link>
        </LegalLinks>
      </BottomBar>
    </FooterContainer>
  )
}
